import { notify } from '@yembo/yemblocks-core';
import { endSession } from 'src/util/endSession';
import { apiUrl } from 'src/util/env';
import { queryClient } from '.';
import { getSessionEnd, getStatuses } from './apiStatus';

type Url = { url: string; urlParams?: URLSearchParams | string };
const DEFAULT_ERROR_MESSAGE =
  'The request was unable to reach our servers. Please check your connection and try again.';

type YemboApiCallOptions = RequestInit &
  Url & {
    shouldNotifyAndThrowError?: boolean;
    withoutAccessToken?: boolean;
    /**
     * Whether a 401 signs the person out. Off only matters for a call sent
     * `withoutAccessToken` (the sign-in screens, where a 401 is an answer about
     * the credentials typed): a 401 on a call that carried the token means the
     * session is gone, and signs out regardless (see getSessionEnd).
     */
    logoutIfNotAuthorized?: boolean;
    timeoutMs?: number;
  };

export const JSON_MUTATION_TIMEOUT_MS = 15000;
export const UPLOAD_TIMEOUT_MS = 90000;

export function getYemboErrorMessage(errorJson: unknown) {
  return getStatuses(errorJson)[0]?.message || DEFAULT_ERROR_MESSAGE;
}

export function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError';
}

export class YemboApiError extends Error {
  constructor(
    readonly response: Response,
    readonly errorJson?: unknown
  ) {
    const errorMessage = getYemboErrorMessage(errorJson);
    super(errorMessage);
  }

  /** The first status type of the error body (`conflict`, `not_found`, …). */
  get type(): string | undefined {
    return getStatuses(this.errorJson)[0]?.type;
  }
}

/**
 * A session that ended (expired, revoked, signed out elsewhere) is reported
 * once, by endSession, rather than by every request that was in flight.
 */
const hasEndedSession = (response: Response, errorJson: unknown, options: Parameters<typeof getSessionEnd>[2]) =>
  getSessionEnd(response.status, errorJson, options) !== null;

/**
 * A success body, read one of two ways:
 *
 * - with `key`, the named field of a composite result (`{ rooms }`,
 *   `{ item }`, `{ employee, device }`…). A 2xx body without it is a contract
 *   mismatch and throws rather than passing the wrong shape on.
 * - without `key`, the body itself: a bare record, or a paged list whose rows
 *   travel with their page metadata.
 *
 * A 204 is null. A 404 is null unless `errorOnNotFound`.
 */
export const queryYemboApiCallWithReturnValue = async <T, K extends string = never>(
  prop: YemboApiCallOptions & { key?: K; errorOnNotFound?: boolean }
): Promise<T | null> => {
  let response: Response;

  try {
    response = await queryYemboApiCall({
      ...prop,
      shouldNotifyAndThrowError: false,
    });
  } catch (error) {
    if (prop.shouldNotifyAndThrowError && !isAbortError(error)) notify.error(DEFAULT_ERROR_MESSAGE);
    throw error;
  }

  if (response.ok) {
    if (response.status === 204) return null;

    try {
      const responseJson: unknown = await response.json();

      if (!prop.key) return responseJson as T;

      if (!responseJson || typeof responseJson !== 'object' || !(prop.key in responseJson)) {
        throw new Error(`${prop.url}: the response has no "${prop.key}"`);
      }

      return (responseJson as Record<K, T>)[prop.key];
    } catch (error) {
      if (prop.shouldNotifyAndThrowError) notify.error(DEFAULT_ERROR_MESSAGE);
      throw error;
    }
  }

  if (response.status === 404 && !prop.errorOnNotFound) {
    return null;
  }

  let errorJson: unknown;

  try {
    errorJson = await response.json();
  } catch {
    errorJson = undefined;
  }

  // endSession may have cleared the token already, so judge by whether the call was allowed to send one.
  const sessionOptions = {
    logoutIfNotAuthorized: prop.logoutIfNotAuthorized,
    sentAccessToken: !prop.withoutAccessToken,
  };

  if (prop.shouldNotifyAndThrowError && !hasEndedSession(response, errorJson, sessionOptions)) {
    notify.error(getYemboErrorMessage(errorJson));
  }

  throw new YemboApiError(response, errorJson);
};

export const queryYemboApiCall = async ({
  method,
  signal,
  headers,
  body,
  url,
  urlParams,
  withoutAccessToken,
  logoutIfNotAuthorized = true,
  shouldNotifyAndThrowError = false,
  shouldThrowError = false,
  timeoutMs,
}: YemboApiCallOptions & { shouldThrowError?: boolean }): Promise<Response> => {
  const token = window.localStorage.getItem('accessToken');
  const impersonatedRole = window.localStorage.getItem('impersonatedRole');
  const impersonatedCompanyKeys = window.localStorage.getItem('impersonatedCompanyKeys');

  const sentAccessToken = !!token && !withoutAccessToken;
  const authHeader: HeadersInit = sentAccessToken ? { 'X-Employee-Access-Token': token } : {};
  const impersonationHeaders: HeadersInit = {};

  if (impersonatedRole) impersonationHeaders['X-Role'] = impersonatedRole;
  if (impersonatedCompanyKeys) impersonationHeaders['X-Company-Keys'] = impersonatedCompanyKeys;

  let notifyMessage: string | undefined;
  let isNotificationSuppressed = false;

  let effectiveSignal = signal;
  let timeoutId;
  let abortHandler;

  if (timeoutMs && !signal?.aborted) {
    const controller = new AbortController();
    effectiveSignal = controller.signal;
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    if (signal) {
      abortHandler = () => controller?.abort();
      signal.addEventListener('abort', abortHandler, { once: true });
    }
  }

  try {
    const response: Response = await fetch(`${apiUrl}/${url}${urlParams ? `?${urlParams.toString()}` : ''}`, {
      method,
      signal: effectiveSignal,
      body,
      headers: {
        ...authHeader,
        ...impersonationHeaders,
        ...headers,
        ...(body instanceof FormData ? undefined : { 'Content-Type': 'application/json' }),
      },
    });

    if (response.ok) return response;

    if (response.status === 401) {
      // Read from a clone: a caller that gets this response back reads the body itself.
      let errorBody: unknown;
      try {
        errorBody = await response.clone().json();
      } catch {
        errorBody = undefined;
      }

      const sessionEnd = getSessionEnd(response.status, errorBody, {
        logoutIfNotAuthorized,
        sentAccessToken,
      });
      if (sessionEnd) endSession(sessionEnd);
    }

    if (!shouldNotifyAndThrowError && !shouldThrowError) return response;

    let jsonError: unknown;
    try {
      jsonError = await response.json();
    } catch {
      jsonError = undefined;
    }

    if (hasEndedSession(response, jsonError, { logoutIfNotAuthorized, sentAccessToken })) {
      isNotificationSuppressed = true;
    }
    notifyMessage = getYemboErrorMessage(jsonError);

    throw new YemboApiError(response, jsonError);
  } catch (error) {
    if (shouldNotifyAndThrowError && !isNotificationSuppressed) notify.error(notifyMessage || DEFAULT_ERROR_MESSAGE);

    throw error;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
    if (signal && abortHandler) signal.removeEventListener('abort', abortHandler);
  }
};

export const invalidateIfAllMutationsDone = (
  queryKey: (string | undefined | boolean)[],
  mutationKey: (string | undefined)[]
) => {
  const shouldInvalidate = queryClient.isMutating({ mutationKey }) === 1;
  if (shouldInvalidate) queryClient.invalidateQueries({ queryKey });
  return shouldInvalidate;
};
